import { WebMercatorViewport } from '@math.gl/web-mercator';
import type { ViewState } from 'react-map-gl';
import type { Bounds } from '../map-helpers';
import { getDefaultViewState } from './getDefaultViewState';

/**
 * Computes a new {@link ViewState} whose center and zoom are adjusted so that
 * the given geographic bounds fit within a viewport of the specified dimensions.
 * Returns the {@link baseViewState} unchanged when no bounds are provided, and
 * preserves the previous zoom level when the computed zoom is out of the
 * valid range (0, 24) or infinite (e.g. for a zero-area bounds).
 * Padding from the base view state is respected unless it exceeds the viewport size.
 *
 * @param baseViewState - The starting view state to derive from. Defaults to {@link getDefaultViewState}.
 * @param bounds - The geographic bounding box to fit into the viewport. When `null` or `undefined`, the baseViewState is returned as-is.
 * @param viewportWidth - The pixel width of the map viewport. Defaults to `1000`.
 * @param viewportHeight - The pixel height of the map viewport. Defaults to `1000`.
 * @returns A new {@link ViewState} fitted to the given bounds, or the baseViewState if no bounds are provided.
 */
export function fitViewStateToBounds({
  baseViewState = getDefaultViewState(),
  bounds,
  viewportHeight = 1000,
  viewportWidth = 1000,
}: {
  baseViewState?: ViewState;
  viewportHeight?: number;
  viewportWidth?: number;
  bounds?: Bounds | null;
}): ViewState {
  if (bounds == null) {
    return baseViewState;
  }

  const oldZoom = baseViewState != null ? baseViewState.zoom : 7;
  const newViewport = {
    ...baseViewState,
    width: viewportWidth,
    height: viewportHeight,
  };
  const oldPadding = baseViewState.padding;

  // Zero out padding if it would consume more than the available viewport space
  const verticalOverflow = viewportHeight - oldPadding.top - oldPadding.bottom < 0;
  const horizontalOverflow = viewportWidth - oldPadding.left - oldPadding.right < 0;

  const padding = {
    top: verticalOverflow ? 0 : oldPadding.top,
    bottom: verticalOverflow ? 0 : oldPadding.bottom,
    left: horizontalOverflow ? 0 : oldPadding.left,
    right: horizontalOverflow ? 0 : oldPadding.right,
  };

  let { longitude, latitude, zoom } = new WebMercatorViewport(newViewport).fitBounds(
    [
      [bounds.minLng, bounds.minLat],
      [bounds.maxLng, bounds.maxLat],
    ],
    {
      padding,
    },
  );

  if (zoom === Number.POSITIVE_INFINITY) zoom = oldZoom;
  if (zoom <= 0) zoom = oldZoom;
  if (zoom >= 24) zoom = oldZoom;

  return {
    ...newViewport,
    latitude,
    longitude,
    zoom,
    padding: { top: 0, bottom: 0, left: 0, right: 0 },
  };
}
